import React, { useEffect, useRef, useState } from 'react';
import RoleMarkdownRenderer from './RoleMarkdownRenderer';
import { Nav, NavItem, NavLink, Spinner } from 'reactstrap';
import useStickyScrollLock from '../hooks/useStickyScrollLock';

function RoleDescriptionPanel({ stickyRef, isUserAuthenticated = false, isApplyDisabled = true, isRoleCapped, submissionStatus, acceptedAcknowledgements = [], roleData, roleDescriptionData = {}, onApply, toggleAcknowledgement, showConfirmModal }) {
  const sectionNavItems = roleDescriptionData.sections?.map(({ id, title }) => ({ id, title })) ?? [];
  const [activeSection, setActiveSection] = useState(null);
  const observerRef = useRef(null);
  const scrollableRef = useRef(null);
  const topOffset = 110;

  useStickyScrollLock({ stickyRef, scrollableRef, topOffset });

  // Reset active section and scroll to top when role changes
  useEffect(() => {
    if (!sectionNavItems.length) return;
    setActiveSection(sectionNavItems[0].id);
    if (scrollableRef.current) scrollableRef.current.scrollTop = 0;

    if (stickyRef?.current) {
      const stickyRect = stickyRef.current.getBoundingClientRect();
      const targetTop = Math.max(0, window.scrollY + stickyRect.top - topOffset);
      window.scrollTo({ top: targetTop, behavior: 'smooth' });
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [roleDescriptionData.roleId]);

  const handleSectionsRender = sections => {
    if (!scrollableRef.current || !sections.length) return;
    if (observerRef.current) observerRef.current.disconnect();
  
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.length) return;
  
        // Find the section closest to the container top
        const containerRectTop = scrollableRef.current.getBoundingClientRect().top;
        let closest = entries[0].target.id;
        let minDistance = Infinity;
  
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const distance = Math.abs(entry.boundingClientRect.top - containerRectTop);
            if (distance < minDistance) {
              minDistance = distance;
              closest = entry.target.id;
            }
          }
        });
  
        setActiveSection(closest);
      },
      {
        root: scrollableRef.current,
        rootMargin: "-35% 0px -10% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
  
    sections.forEach(section => observer.observe(section));
    observerRef.current = observer;
  };

  const scrollToSection = roleId => {
    const container = scrollableRef.current;
    if (!container) return;

    const safeId = window.CSS?.escape ? CSS.escape(roleId) : roleId;
    const section = container.querySelector(`#${safeId}`);

    if (section) {
      const containerRect = container.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const deltaTop = sectionRect.top - containerRect.top;
      const targetTop = container.scrollTop + deltaTop - 12;
      container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }
    setActiveSection(roleId);
  };

  return (
    <div className="role-description-panel d-flex gap-3">
      {roleDescriptionData.roleId ? (
        <>
          {/* LEFT NAV */}
          <div className="d-none d-lg-block">
            {sectionNavItems.length > 0 && (
              <Nav vertical>
                {sectionNavItems.map(({ id, title }) => (
                  ((!isRoleCapped && roleData.isRegistrationOpen) || id === 'overview') && (
                    <NavItem key={`nav-item-${id}`}>
                      <NavLink
                        onClick={() => scrollToSection(id)}
                        style={{ cursor: 'pointer' }}
                        className={`text-nowrap small ${activeSection === id ? "text-primary-orange-emphasis" : "text-secondary"}`}
                      >
                        {title}
                      </NavLink>
                    </NavItem>
                  )))}
              </Nav>
            )}
          </div>

          {/* MARKDOWN CONTENT */}
            <RoleMarkdownRenderer
              role={roleData}
              isUserAuthenticated={isUserAuthenticated}
              isApplyDisabled={isApplyDisabled}
              submissionStatus={submissionStatus}
              descriptionData={roleDescriptionData}
              isRoleCapped={isRoleCapped}
              scrollableRef={scrollableRef}
              onSectionsRender={handleSectionsRender}
              scrollToSection={scrollToSection}
              onApply={onApply}
              acceptedAcknowledgements={acceptedAcknowledgements}
              toggleAcknowledgement={toggleAcknowledgement}
            showConfirmModal={showConfirmModal}
          />
        </>
      ) : (
        <div className="d-flex justify-content-center align-items-center gap-2 text-secondary mx-auto">
          <Spinner className="text-primary-orange" />
        </div>
      )}
    </div>
  );
}

export default RoleDescriptionPanel;
